import { Nullable } from "common/records";
import { calculateMonthlyPayment, EstimateFinanceOptionRecord, fromJSON } from "common/records/EstimateFinanceOption";
import { calculateRate, FinanceOptionRecord } from "common/records/FinanceOption";
import { RootActions, RootState } from "common/reducers";
import { model as estimateModel } from "common/reducers/estimate.reducer";
import {
  ADD_ESTIMATE_FINANCE_OPTION,
  CALCULATE_ESTIMATE_FINANCE_OPTION,
  DUPLICATE_ESTIMATE_FINANCE_OPTION,
  RECEIVE_ESTIMATE_FINANCE_OPTION,
  REMOVE_ESTIMATE_FINANCE_OPTION,
} from "common/reducers/estimateFinanceOption.actions";
import { estimate } from "common/selectors/estimate.selectors";
import { unsavedId as unsavedIdSelector } from "common/selectors/estimateFinanceOption.selectors";
import { financeOption } from "common/selectors/financeOption.selectors";
import { List, Map, Record } from "immutable";

export const EstimateFinanceOptionStateRecord = Record({
  byId: Map<number, EstimateFinanceOptionRecord>(),
  lastSavedById: Map<number, EstimateFinanceOptionRecord>(),
  unsavedId: -1,
});

export const initialState = EstimateFinanceOptionStateRecord();
export type EstimateFinanceOptionState = typeof initialState;

export const model = "estimateFinanceOptions";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ESTIMATE_FINANCE_OPTION: {
      const estimateFinanceOption = fromJSON({ ...action.payload.estimateFinanceOption });
      state = state.setIn([model, "byId", estimateFinanceOption.id], estimateFinanceOption);
      state = state.setIn([model, "lastSavedById", estimateFinanceOption.id], estimateFinanceOption);
      return state;
    }

    case ADD_ESTIMATE_FINANCE_OPTION: {
      const { estimateId, financeOptionId } = action.payload;

      // Get the org finance option to copy data from
      const orgFinanceOption = financeOption(state, { financeOptionId });
      if (!orgFinanceOption) {
        console.error("Cannot add estimate finance option: finance option not found", { financeOptionId });
        return state;
      }

      // Get the next unsaved ID
      const unsavedId = state.getIn([model, "unsavedId"]) as number;

      // Create the new estimate finance option
      const estimateFinanceOption = fromJSON({
        id: unsavedId,
        estimate_id: estimateId,
        finance_option_id: financeOptionId,
        finance_option: orgFinanceOption.toJS() as any,
      });

      state = state.setIn([model, "byId", estimateFinanceOption.id], estimateFinanceOption);
      state = state.setIn([model, "unsavedId"], unsavedId - 1);

      return state;
    }

    case REMOVE_ESTIMATE_FINANCE_OPTION: {
      const { estimateFinanceOptionId } = action.payload;
      return state.setIn([model, "byId", estimateFinanceOptionId, "_destroy"], true);
    }

    case DUPLICATE_ESTIMATE_FINANCE_OPTION: {
      const { sourceEstimateFinanceOptionId, targetEstimateId } = action.payload;

      const source: EstimateFinanceOptionRecord | undefined = state.getIn([
        model,
        "byId",
        sourceEstimateFinanceOptionId,
      ]);
      if (!source || source._destroy) {
        return state;
      }

      const unsavedId = unsavedIdSelector(state);

      const duplicated = source.merge({
        id: unsavedId,
        estimate_id: targetEstimateId,
        _destroy: null,
      });

      state = state.setIn([model, "byId", unsavedId], duplicated);
      state = state.setIn([model, "unsavedId"], unsavedId - 1);

      const targetEstimate = estimate(state, { estimateId: targetEstimateId });
      if (targetEstimate) {
        state = state.setIn(
          [estimateModel, "byId", targetEstimateId, "finance_option_ids"],
          targetEstimate.finance_option_ids.push(unsavedId),
        );
      }

      return state;
    }

    case CALCULATE_ESTIMATE_FINANCE_OPTION: {
      const { estimateId, estimateFinanceOptionId } = action.payload;

      // Get the estimate finance option
      const estimateFinanceOption = state.getIn([model, "byId", estimateFinanceOptionId]) as
        | EstimateFinanceOptionRecord
        | undefined;
      if (!estimateFinanceOption) {
        console.error("Cannot calculate: estimate finance option not found", { estimateFinanceOptionId });
        return state;
      }

      // Get the estimate to get financed_amount
      const estimateRecord = estimate(state, { estimateId });
      if (!estimateRecord) {
        console.error("Cannot calculate: estimate not found", { estimateId });
        return state;
      }

      const financedAmount = estimateRecord.payment_terms?.financed_amount ?? estimateRecord.total;
      // rsf-api omits the nested finance_option unless include[]=finance_option is sent.
      const fo: Nullable<FinanceOptionRecord> =
        estimateFinanceOption.finance_option ??
        financeOption(state, { financeOptionId: estimateFinanceOption.finance_option_id });

      if (!fo) {
        console.error("Cannot calculate: finance option data not found on estimate finance option");
        return state;
      }

      const monthlyPayment = fo.formula
        ? calculateRate(fo, estimateRecord)
        : calculateMonthlyPayment(financedAmount, fo.rate, fo.term);
      const promoPayment = calculateMonthlyPayment(financedAmount, fo.promo_rate ?? 0, fo.promo_term ?? 0);

      // Update the estimate finance option with calculated values
      const updatedEstimateFinanceOption = estimateFinanceOption.merge({
        amount: financedAmount,
        monthly_payment: monthlyPayment,
        promo_payment: promoPayment,
      });

      return state.setIn([model, "byId", estimateFinanceOptionId], updatedEstimateFinanceOption);
    }

    default:
      return state;
  }
};
